// The group training matrix joins two independently fetched payloads: catalog rows from
// findCoursesByCampus become the columns, and completion records from findTrainingsByOrg fill the
// cells. Course code alone cannot key that join — the same course is routinely classified under
// several categories, and each classification is its own column with its own completion record. So
// every key here is (classification, course code), which the database guarantees to be unique per
// campus via the unique index on Learning_ClassifiedCourses.

const KEY_SEPARATOR = '|';

// Merged course families come back from the server as a single string, e.g.
// "Hazardous Waste - IRC-EHS-LT-HW, IRC-EHS-LT-HWT". Split off just the trailing
// code list (kept only when the canonical activityCode is actually one of the
// codes, to avoid mistaking a name that naturally contains " - " for a merged one).
const MERGED_CODE_LIST_PATTERN = / - ([A-Za-z0-9_.-]+(?:,\s*[A-Za-z0-9_.-]+)+)$/;

export const splitMergedCourseCodes = (activityName, activityCode) => {
  const match = activityName?.match(MERGED_CODE_LIST_PATTERN);
  if (!match) return { baseName: activityName, codes: [] };

  const codes = match[1].split(',').map((code) => code.trim());
  if (!codes.includes(activityCode)) return { baseName: activityName, codes: [] };

  return { baseName: activityName.slice(0, match.index), codes };
};

const getCourseKey = (classificationId, activityCode) =>
  `${classificationId ?? ''}${KEY_SEPARATOR}${activityCode ?? ''}`;

/** Key for a catalog row, used as the column's prop and as its cell lookup. */
export const getCourseColumnKey = ({ classificationId, activityCode }) => getCourseKey(classificationId, activityCode);

/** Key for a completion record, named after the fields the trainings payload uses. */
export const getTrainingCourseKey = ({ classifId, srcActivityCode }) => getCourseKey(classifId, srcActivityCode);

/**
 * A merged column stands in for every code in its family, so each family member's key has to
 * resolve to the surviving column's key. Without this, a completion record filed under a
 * non-primary code (IRC-EHS-LT-HWT) finds no column (IRC-EHS-LT-HW) and its cell renders empty.
 */
export const getCourseKeyAliases = (courses = []) => {
  const aliases = new Map();

  courses.forEach((course) => {
    const { codes } = splitMergedCourseCodes(course.activityName, course.activityCode);
    codes.forEach((code) => aliases.set(getCourseKey(course.classificationId, code), getCourseColumnKey(course)));
  });

  return aliases;
};

/** Resolves a completion record to the column it belongs in. */
export const resolveCourseKey = (training, aliases) => {
  const key = getTrainingCourseKey(training);
  return aliases?.get(key) || key;
};

/**
 * Two classifications of the same course are two separate columns, so the course name on its own is
 * an ambiguous header — the classification is what tells them apart. A merged family keeps its code
 * list in the tooltip rather than the header, which would otherwise read "name - HW, HWT - Biosafety".
 */
export const getCourseLabel = ({ activityName, activityCode, classificationName }) => {
  const { baseName } = splitMergedCourseCodes(activityName, activityCode);
  return classificationName ? `${baseName} - ${classificationName}` : baseName;
};
